<template>
    <teleport to="body">
        <div data-testid="toast-container"
            class="fixed z-[9999] pointer-events-none top-2.5 right-2.5 left-2.5 sm:top-5 sm:right-5 sm:left-auto">
            <transition-group name="toast" tag="div">
                <div v-for="toast in toasts" :key="toast.id" :class="[
                    'toast flex items-start gap-3 mb-3 p-4 rounded-lg shadow-lg border-l-4 bg-white relative overflow-hidden pointer-events-auto',
                    {
                        'min-w-[300px] max-w-[400px] sm:min-w-[300px] sm:max-w-[400px]': true,
                        'toast-dismissible': toast.dismissible,
                        'toast-error border-red-500 bg-red-50': toast.type === 'error',
                        'toast-success border-green-500 bg-green-50': toast.type === 'success',
                        'toast-warning border-yellow-500 bg-yellow-50': toast.type === 'warning',
                        'toast-info border-blue-500 bg-blue-50': toast.type === 'info',
                    }
                ]">
                    <!-- Toast Icon -->
                    <div class="toast-icon w-6 h-6 mt-0.5 shrink-0">
                        <svg v-if="toast.type === 'error'" class="toast-icon-svg w-full h-full text-red-500" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <svg v-else-if="toast.type === 'success'" class="toast-icon-svg w-full h-full text-green-500"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <svg v-else-if="toast.type === 'warning'" class="toast-icon-svg w-full h-full text-yellow-500"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <svg v-else class="toast-icon-svg w-full h-full text-blue-500" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>

                    <!-- Toast Content -->
                    <div class="toast-content flex-1 min-w-0">
                        <div v-if="toast.title" class="toast-title font-semibold text-sm mb-1" :class="{
                            'text-red-600': toast.type === 'error',
                            'text-green-600': toast.type === 'success',
                            'text-yellow-600': toast.type === 'warning',
                            'text-blue-600': toast.type === 'info',
                            'text-gray-700': !toast.type
                        }">{{ toast.title }}</div>
                        <div class="toast-message text-sm leading-snug break-words" :class="{
                            'text-red-700': toast.type === 'error',
                            'text-green-700': toast.type === 'success',
                            'text-yellow-700': toast.type === 'warning',
                            'text-blue-700': toast.type === 'info',
                            'text-gray-500': !toast.type
                        }">{{ toast.message }}</div>
                    </div>

                    <!-- Close Button -->
                    <button v-if="toast.dismissible" @click.stop="removeToast(toast.id)"
                        class="toast-close w-5 h-5 mt-0.5 p-0 text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Fechar notificação">
                        <svg class="toast-close-icon w-full h-full" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <!-- Progress Bar (for auto-dismiss) -->
                    <div v-if="toast.duration && toast.duration > 0"
                        class="toast-progress absolute bottom-0 left-0 h-[3px] opacity-30"
                        :style="{ animationDuration: `${toast.duration}ms` }" :class="{
                            'bg-red-500': toast.type === 'error',
                            'bg-green-500': toast.type === 'success',
                            'bg-yellow-500': toast.type === 'warning',
                            'bg-blue-500': toast.type === 'info',
                        }"></div>
                </div>
            </transition-group>
        </div>
    </teleport>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useToast } from '@/core/composables/use-toast'

export default defineComponent({
    name: 'ToastContainer',
    setup() {
        const { toastState, removeToast } = useToast()

        const toasts = computed(() => toastState.toasts)

        return {
            toasts,
            removeToast
        }
    }
})
</script>

<style scoped>

/* Toast Icon */
.toast-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin-top: 2px;
}

.toast-icon-svg {
    width: 100%;
    height: 100%;
}


/* Toast Content */
.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 4px;
    color: #374151;
}

.toast-message {
    font-size: 14px;
    line-height: 1.4;
    color: #6b7280;
    word-wrap: break-word;
}


/* Close Button */
.toast-close {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    padding: 0;
    margin: 0;
    margin-top: 2px;
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    transition: color 0.2s;
}



/* Progress Bar */
.toast-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: currentColor;
    opacity: 0.3;
    animation: toast-progress linear forwards;
}

/* Toast Types */
.toast-error {
    border-left-color: #ef4444;
    background: #fef2f2;
}

.toast-success {
    border-left-color: #22c55e;
    background: #f0fdf4;
}

.toast-warning {
    border-left-color: #f59e0b;
    background: #fffbeb;
}

.toast-info {
    border-left-color: #3b82f6;
    background: #eff6ff;
}

.toast-error .toast-title {
    color: #dc2626;
}

.toast-error .toast-message {
    color: #991b1b;
}

.toast-success .toast-title {
    color: #16a34a;
}

.toast-success .toast-message {
    color: #15803d;
}

.toast-warning .toast-title {
    color: #d97706;
}

.toast-warning .toast-message {
    color: #92400e;
}

.toast-info .toast-title {
    color: #2563eb;
}

.toast-info .toast-message {
    color: #1d4ed8;
}

.toast-error .toast-progress {
    color: #ef4444;
}

.toast-success .toast-progress {
    color: #22c55e;
}

.toast-warning .toast-progress {
    color: #f59e0b;
}

.toast-info .toast-progress {
    color: #3b82f6;
}

@keyframes toast-progress {
    from {
        width: 100%;
    }

    to {
        width: 0%;
    }
}

/* Transitions */
.toast-enter-active {
    transition: all 0.3s ease-out;
}

.toast-leave-active {
    transition: all 0.3s ease-in;
}

.toast-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.toast-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.toast-move {
    transition: transform 0.3s ease;
}
</style>